/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DoneInvokeEvent,
  type ActivityConfig,
  type PayloadSender,
  type State,
  type Typestate,
} from 'xstate';
import {
  type ToastProps,
  Button,
  Divider,
  Spacer,
  Stack,
  TextBody,
} from '@pypestream/design-system';
import { CombinedError } from '@pypestream/api-services/urql';
import { i18n } from '@pypestream/translations';
import type {
  AvailableFeatureFlagKeys,
  FeatureFlagsType,
} from '@pypestream/feature-flags';

import { getXstateUtils, type MachineStateSchemaPaths } from './xstate.utils';
import { type UserContext } from './user.xstate';
import { ProStudioContext } from './prostudio.xstate.types';

export interface UserInfo {
  displayName?: string;
  firstName?: string;
  lastName?: string;
  profilePhoto?: string;
}

/**
 * These State Machine IDs are invoked from within the App machine
 * Most are invoked right when the App machine starts and are global always-on machines. These are the the sub-machines that get custom React hooks made and exported out.
 * Others are invoked in certain states.
 * These IDs are used for sending events directly to them.
 */
export enum GLOBAL_APP_SUB_MACHINE_IDS {
  ui = 'ui',
  user = 'user',
  proStudio = 'pro-studio',
}

type Account = {
  id: string;
  apps: string[];
};

type App = {
  id: string;
  name: string;
  versions?: string[];
  latestVersion?: string;
  canPublish?: boolean;
  activeVersions?: string[];
  inactiveVersions?: string[];
};

export interface AccountLoadResults {
  accounts: Account[];
}

export interface AppLoadResults {
  apps: App[];
}

export interface GlobalAppContext {
  accounts?: AccountLoadResults;
  apps?: AppLoadResults;
  featureFlags?: FeatureFlagsType;
}

export interface GlobalAppStateSchema {
  // eslint-disable-next-line
  states: {
    featureFlags: {
      states: {
        idle: Record<string, unknown>;
        loaded: {
          states: {
            withActualValue: Record<string, unknown>;
            // With error state, we will initialized all featureFlags with default compile time values.
            withDefaultValues: Record<string, unknown>;
          };
        };
      };
    };
  };
}

export type SharedEvents =
  // Sometimes we need to return an event that does nothing
  | { type: 'noop' }
  //   }
  | {
      isSessionEndedEvent?: boolean;
      redirectURL?: string;
      type: 'user.signOut';
    }
  | {
      type: 'user.signIn';
      user: UserContext['user'];
    }
  | {
      type: 'user.assignedUserId';
      userId: NonNullable<UserContext['user']>['id'];
    }
  | {
      type: 'reset.errors';
    }
  | { type: 'user.loggedIn' }
  | { type: 'user.setProfilePhoto'; profilePhoto: string | undefined }
  | { type: 'user.authError'; errorMsg: string }
  | {
      type: 'featureFlags.initialized';
      flags: FeatureFlagsType;
    }
  | { type: 'featureFlags.initializedWithDefaultValues' }
  | {
      type: 'featureFlags.update';
      updatedFlag: AvailableFeatureFlagKeys;
      updatedValue: FeatureFlagsType[AvailableFeatureFlagKeys];
    };

export type GlobalAppEvents =
  | { type: 'app.log'; msg: any }
  | { type: 'app.sendUserMessage'; msg: ToastProps }
  | { type: 'navigate.log'; msg: any }
  | SharedEvents;

/** All `AppEvents` whose `type` starts with `user` followed by other strings */
export type GlobalAppUserEvents = Extract<
  GlobalAppEvents,
  { type: `user.${string}` }
>;

/**
 * All state values as strings in dot notation, i.e. `'user.loggedIn' | 'user.loggedIn.loading'`
 * These strings are passed to `state.matches('user.loggedIn')` which returns a `boolean`
 * If `state.matches()` is not typed correctly, use this type:
 * @example
 * const isLoggedIn = state.matches<AppStateValues>('user.loggedIn');
 */
export type GlobalAppStateValues = MachineStateSchemaPaths<
  GlobalAppStateSchema['states']
>;
export interface GlobalAppTypestates extends Typestate<GlobalAppContext> {
  context: GlobalAppContext;
  value: GlobalAppStateValues;
}

export type GlobalAppState = State<
  GlobalAppContext,
  GlobalAppEvents,
  GlobalAppStateSchema,
  GlobalAppTypestates
>;

export type GlobalAppSender = PayloadSender<GlobalAppEvents>;

export const {
  createAction,
  createInvokablePromise,
  createXstateHooks: createAppXstateHooks,
} = getXstateUtils<
  GlobalAppContext,
  GlobalAppEvents,
  GlobalAppTypestates,
  GlobalAppStateSchema
>();

export type Activity = ActivityConfig<GlobalAppContext, GlobalAppEvents>;

const exampleActivity: Activity = (_ctx, _activity): ReturnType<Activity> => {
  // Start the beeping activity
  const interval = setInterval(() => {
    // console.log('BEEP!');
  }, 2000);

  // Return a function that stops the beeping activity
  return () => clearInterval(interval);
};

export const appActivities: Record<
  'exampleActivity',
  { id: string; exec: Activity }
> = {
  exampleActivity: {
    id: 'exampleActivity',
    exec: exampleActivity,
  },
};

export function sendUserMessage(props: ToastProps): void {
  import('@pypestream/design-system').then(({ sendToast }) => sendToast(props));
}

/**
 * Re-renders a toast that is already on screen, addressed by `toastId`. Used for progress
 * that changes while the toast stays up (publish steps) — `sendToast` with a live id is a
 * no-op, so re-sending would silently keep the first text. Does nothing if no such toast is
 * open, which is the desired behaviour for a late update.
 */
export function updateUserMessage(
  props: ToastProps & { toastId: string }
): void {
  import('@pypestream/design-system').then(({ updateToast }) =>
    updateToast(props)
  );
}

export const globalAppActions = {
  log: createAction<{
    level?: 'warn' | 'error' | 'debug';
    msg: string;
    details?: Record<string, unknown>;
  }>({
    id: 'log',
    exec({ action, event, ctx, state }) {
      const { level = 'log', msg, details } = action;
      console[level](`${msg}. Event: ${event.type}`, {
        event,
        ctx,
        details,
        state,
      });
    },
  }),
  logError: createAction({
    id: 'logError',
    exec({ event }) {
      const { data: error } = event as { type: string; data: Error };
      if (error instanceof Error) {
        console.error(error);
      } else {
        console.error(event);
      }
    },
  }),
};

function extractErrorId(message: string): string | null {
  const match = message.match(/Error ID:\s*([a-f0-9-]{36})/i);
  return match ? match[1] : null;
}

function splitErrorMessage(fullMessage: string): {
  mainMessage: string;
  supportInfo: string | null;
} {
  const supportInfoPattern = /\(Error ID: [^)]+\)/;
  const match = fullMessage.match(supportInfoPattern);

  if (!match) {
    return {
      mainMessage: fullMessage.trim(),
      supportInfo: null,
    };
  }

  const supportInfo = match[0];
  const mainMessage = fullMessage.replace(supportInfoPattern, '').trim();

  return {
    mainMessage,
    supportInfo,
  };
}

type ToastMessageProps = Omit<ToastProps, 'text'> & {
  withCopyId?: boolean;
  text?: string;
  /**
   * PSTUDIO-4122: a full `scope/file:key` translation key, resolved when the
   * action RUNS rather than when the machine is built.
   *
   * The distinction is load-bearing: `showToastMessage({...})` is evaluated at
   * module load, while the machine's config is constructed — before
   * `init()` has registered any catalogue — so a `t(...)` passed in as `text`
   * would resolve to the bare key and stay that way for the life of the tab.
   * Resolving inside `exec` is what makes the translated string correct, and it
   * is also what lets a language switch take effect without a reload.
   *
   * `@pypestream/design-system`'s `sendToast` takes plain strings and has no
   * `i18nKey` of its own, and this layer has no React, so the imperative
   * singleton is the only route to a translation here.
   */
  i18nKey?: string;
};

export const showToastMessage = ({
  text,
  i18nKey,
  type = 'error',
  autoClose = 3000,
}: ToastMessageProps) => {
  const handleCopyErrorId = (id: string | null) => {
    if (!id) {
      console.error('No error ID found');
      return;
    }

    navigator.clipboard.writeText(id);

    sendUserMessage({
      type: 'success',
      text: 'Error ID copied to clipboard',
      autoClose,
    });
  };

  return {
    type: 'sendUserMessage',
    exec(_ctx: ProStudioContext, event: DoneInvokeEvent<CombinedError>) {
      // Resolved here, not in the argument list — see `i18nKey` above for why
      // the difference decides whether the user sees prose or a raw key.
      const heading = i18nKey ? i18n.t(i18nKey) : (text ?? '');
      const errorMessage = `${heading}${event.data?.message ? `: ${event.data?.message}` : ''}`;

      const errorId = extractErrorId(errorMessage);

      sendUserMessage({
        type,
        text: errorId ? '' : errorMessage,
        autoClose,
        action: errorId
          ? (() => {
              const { mainMessage, supportInfo } =
                splitErrorMessage(errorMessage);
              return (
                <>
                  <TextBody>{mainMessage}</TextBody>
                  <Spacer size="small" />
                  <Divider />
                  <Spacer size="small" />
                  <TextBody>{supportInfo}</TextBody>
                  <Stack
                    direction="row"
                    alignItems="end"
                    justifyContent="end"
                    gutter="small"
                  >
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={() => handleCopyErrorId(errorId)}
                    >
                      Copy Error Id
                    </Button>
                  </Stack>
                </>
              );
            })()
          : undefined,
      });
    },
  };
};
